import { isSafeInlineLinkHref } from '@/hen-ui/lib/inline-copy';
import type { NavItem, NavItemLayout, SubNavCta } from './types';

const VIEW_ALL_PATTERN = /\bview\s+all\b/i;

const NAV_ITEM_TYPES = new Set<NonNullable<NavItem['type']>>([
  'featured',
  'action',
  'highlighted',
]);

const NAV_ITEM_LAYOUTS = new Set<NavItemLayout>(['sections', 'columns']);

const NAV_HREF_SCHEME = /^(https?|mailto|tel):/i;
const ROOT_RELATIVE_PATH = /^\/(?![/\\])/;

/**
 * A catalog URL is a link only when it is root-relative (`/…`, `?…`), an
 * in-page fragment (`#…`), or uses http(s), mailto, or tel. Scheme-less hosts
 * (`example.com`, `www.example.com/x`) would otherwise resolve as relative paths
 * and 404.
 */
function isAuthorableNavHref(uri: string): boolean {
  if (!isSafeInlineLinkHref(uri)) return false;
  if (NAV_HREF_SCHEME.test(uri)) return true;
  if (ROOT_RELATIVE_PATH.test(uri)) return true;
  return uri.startsWith('?') || uri.startsWith('#');
}

/**
 * Returns the trimmed `href` when it is an authorable nav URL, otherwise
 * `'#'`. Use for every catalog-authored nav href (items, flat links, CTA).
 */
export function normalizeNavHref(uri: unknown): string {
  if (typeof uri !== 'string') return '#';
  const trimmed = uri.trim();
  return isAuthorableNavHref(trimmed) ? trimmed : '#';
}

function normalizeNavItemList(items: unknown[], pathPrefix: string): NavItem[] {
  let unchanged = true;
  const normalized: NavItem[] = [];

  items.forEach((item, index) => {
    const next = normalizeNavItem(item, `${pathPrefix}-${index}`);
    if (!next) {
      unchanged = false;
      return;
    }
    if (next !== item) unchanged = false;
    normalized.push(next);
  });

  return unchanged ? (items as NavItem[]) : normalized;
}

function normalizeNavItem(item: unknown, path: string): NavItem | null {
  if (typeof item === 'string' || item == null || typeof item !== 'object') {
    const label = typeof item === 'string' ? item.trim() : '';
    if (!label) return null;
    return { id: path, label, uri: '#' };
  }

  const record = item as NavItem;
  const { type, layout, children, ...rest } = record;
  const id =
    typeof record.id === 'string' && record.id.trim() ? record.id : path;
  const label = typeof record.label === 'string' ? record.label.trim() : '';
  if (!label) return null;
  const uri = normalizeNavHref(record.uri);
  const validType = type && NAV_ITEM_TYPES.has(type) ? type : undefined;
  const validLayout =
    layout && NAV_ITEM_LAYOUTS.has(layout) ? layout : undefined;
  const normalizedChildren = Array.isArray(children)
    ? normalizeNavItemList(children, id)
    : undefined;

  if (
    id === record.id &&
    label === record.label &&
    uri === record.uri &&
    validType === type &&
    validLayout === layout &&
    normalizedChildren === children
  ) {
    return record;
  }

  return {
    ...rest,
    id,
    label,
    uri,
    ...(validType ? { type: validType } : {}),
    ...(validLayout ? { layout: validLayout } : {}),
    ...(normalizedChildren ? { children: normalizedChildren } : {}),
  };
}

/**
 * Makes catalog-authored items safe to render. Authors never type ids or
 * `#` placeholders, and the editor emits `''` for unset selects, so this
 * fills missing ids from the tree position, turns a missing, unsafe, or
 * scheme-less `uri` (e.g. `javascript:`, `example.com/shop`) into `'#'` (rendered
 * as text, not a link), drops a blank label, and drops unrecognised `type` /
 * `layout`
 * values. Items that need no changes are returned as the same objects, so
 * adapter-supplied trees keep their identity across renders.
 */
export function normalizeNavItems(items: unknown): NavItem[] {
  if (!Array.isArray(items)) return [];
  return normalizeNavItemList(items, 'nav');
}

export function isColumnsLayout(item: NavItem | null | undefined): boolean {
  return item?.layout === 'columns';
}

/** An item opens a submenu when it has inline children. */
export function hasNavSubmenu(
  item: NavItem | null | undefined,
): item is NavItem & { children: NavItem[] } {
  if (!item) return false;
  return (item.children?.length ?? 0) > 0;
}

/**
 * The catalog editor emits the CTA object with blank fields when authors
 * leave it empty, so presence of the object alone doesn't mean there is a
 * footer to show.
 */
export function hasSubNavCtaMessage(
  cta: SubNavCta | null | undefined,
): cta is SubNavCta {
  return typeof cta?.text === 'string' && cta.text.trim().length > 0;
}

export function isSpecialItem(item: NavItem): boolean {
  return VIEW_ALL_PATTERN.test(item.label);
}

export function findSpecialItem(items: NavItem[] | undefined): NavItem | null {
  if (!items) return null;
  return items.find((item) => isSpecialItem(item)) ?? null;
}

export function filterSections(sections: NavItem[], query: string): NavItem[] {
  const lower = query.toLowerCase();

  return sections.reduce<NavItem[]>((acc, section) => {
    if (section.label.toLowerCase().includes(lower)) {
      acc.push(section);
      return acc;
    }

    const matchedColumns = (section.children ?? []).reduce<NavItem[]>(
      (colAcc, column) => {
        if (column.label.toLowerCase().includes(lower)) {
          colAcc.push(column);
          return colAcc;
        }

        const matchedGrandchildren = (column.children ?? []).filter((item) =>
          item.label.toLowerCase().includes(lower),
        );
        if (matchedGrandchildren.length > 0) {
          colAcc.push({ ...column, children: matchedGrandchildren });
        }

        return colAcc;
      },
      [],
    );

    if (matchedColumns.length > 0) {
      acc.push({ ...section, children: matchedColumns });
    }

    return acc;
  }, []);
}

export function hasValidHref(item: NavItem): boolean {
  return Boolean(item.uri && item.uri !== '#');
}

/**
 * A column is "featured" when the column itself is typed `'featured'` or
 * when it groups children that are typed `'featured'`. The `type` field is
 * set by the consumer's adapter layer (e.g. the app's `menuItemToNavItem`).
 */
export function isFeaturedColumn(item: NavItem): boolean {
  return (
    item.type === 'featured' ||
    (item.children?.some((c) => c.type === 'featured') ?? false)
  );
}
